






































































































/* eslint-disable */
import Vue, { PropType } from 'vue';
import { WithRefs } from 'vue-typed-refs';
import { nanoid } from 'nanoid';

import SldsButton from '../Button/Button.vue';
import SldsUtilityLabel from '../UtilityLabel/UtilityLabel.vue';

import Input_InnerInput from './Input_InnerInput.vue';

export type Refs = {
  input: InstanceType<typeof Input_InnerInput>;
};

/**
 * A styled HTML `<input>` element for freeform data entry with label, help
 * text, and error handling.
 *
 * @event inputRef This callback exposes the input reference / DOM node to parent components. `<Parent inputRef={(inputComponent) => this.input = inputComponent} />`
 *
 * @slot default - Elements are added after the `input`.
 * @slot errorText - Message to display when the input is in an error state.
 * @slot fieldLevelHelpTooltip - A `Tooltip` component that is displayed next to the label.
 * @slot fixedTextLeft - Displays text or node to the left of the input. This follows the fixed text input UX pattern.
 * @slot fixedTextRight - Displays text or node to the right of the input. This follows the fixed text input UX pattern.
 * @slot iconLeft - Left aligned icon, must be instace of `design-system-react/components/icon/input-icon`
 * @slot iconRight - Right aligned icon, must be instace of `design-system-react/components/icon/input-icon`
 * @slot inlineEditTrigger
 * @slot inlineHelpText - Displays help text under the input.
 *
 * @see https://lightningdesignsystem.com/components/input/
 */
export default (Vue as WithRefs<Refs>).extend({
  components: {
    SldsButton,
    SldsUtilityLabel,
    Input_InnerInput,
  },
  inheritAttrs: false,
  props: {
    /**
     * The `aria-describedby` attribute is used to indicate the IDs of the elements that describe the object. It is used to establish a relationship between widgets or groups and text that described them. This is very similar to aria-labelledby: a label describes the essence of an object, while a description provides more information that the user might need.
     */
    'aria-describedby': String,
    /**
     * **Assistive text for accessibility**
     * * `label`: Visually hidden label but read out loud by screen readers.
     * * `spinner`: Text for loading spinner icon.
     */
    assistiveText: {
      type: Object as PropType<{
        label: string;
        spinner: string;
      }>,
      default() {
        return {};
      },
    },
    /**
     * Class names to be added to the outer container of the input.
     */
    containerClassName: {},
    /**
     * Custom styles to be passed to the component container
     */
    containerStyle: {
      type: Object as PropType<Partial<CSSStyleDeclaration>>,
    },
    /**
     * Message to display when the input is in an error state. When this is present, also visually highlights the component as in error.
     */
    errorText: String,
    /**
     * Displays text or node to the left of the input. This follows the fixed text input UX pattern.
     */
    fixedTextLeft: String,
    /**
     * Displays text or node to the right of the input. This follows the fixed text input UX pattern.
     */
    fixedTextRight: String,
    /**
     * If true, loading spinner appears inside input on right hand side.
     */
    hasSpinner: Boolean,
    /**
     * Every input must have a unique ID in order to support keyboard navigation and ARIA support.
     */
    id: {
      type: String,
      default() {
        return nanoid();
      },
    },
    /**
     * Displays help text under the input.
     */
    inlineHelpText: String,
    /**
     * Class names to be added to the the input element.
     */
    inputClassName: {},
    /**
     * styles to be added to input
     */
    inputStyle: {
      type: Object as PropType<Partial<CSSStyleDeclaration>>,
    },
    /**
     * Displays the value of the input statically. This follows the static input UX pattern.
     */
    isStatic: Boolean,
    /**
     * This label appears above the input.
     */
    label: String,
    /**
     * Displays the value of the input as read-only. This is used in the inline edit UX pattern.
     */
    readonly: Boolean,
    /**
     * Highlights the input as a required field (does not perform any validation).
     */
    required: Boolean,
    /**
     * The `<Input>` element includes support for all HTML5 types.
     */
    type: {
      type: String,
      validator(val: string) {
        return [
          'text',
          'password',
          'datetime',
          'datetime-local',
          'date',
          'month',
          'time',
          'week',
          'number',
          'email',
          'url',
          'search',
          'tel',
          'color',
        ].includes(val);
      },
      default: 'text',
    },
    /**
     * The input value, bound with `v-model`.
     */
    value: [String, Number],
    /**
     * Which UX pattern of input? The default is `base` while other option is `counter`
     */
    variant: {
      type: String,
      validator(val: string) {
        return ['base', 'counter'].includes(val);
      },
      default: 'base',
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    errorId(): string {
      return this['aria-describedby'] || `${this.id}-error`;
    },
  },
  methods: {
    clear() {
      this.$emit('input', '');
    },
    focus() {
      this.$refs.input.$refs.input.focus();
    },
  },
});
